import _                                                    from 'underscore';
import React, { FC, ReactNode, useEffect, useLayoutEffect } from 'react';
import { useQuery, QueryHookOptions }                       from '@apollo/react-hooks';
import { ApolloError, ApolloQueryResult }                   from 'apollo-client';

import { queriesHaveData, queriesHaveErrors, queriesLoading } from '../../../store/_apolloUtil';
import { AsTagProp, GQLNode }                                 from '../../../types';
import { LoadingIndicator }                                   from '../util/loading/LoadingIndicator';
import { ErrorMessageFromGraphQL }                            from '../util/errorHandling/ErrorMessageFromGraphQL';
import { runIfFunction }                                      from '../../../util';
import { QueryResult }                                        from '@apollo/react-common';

export interface ItemFromQueryProps {
	as?: AsTagProp,
	query: GQLNode,
	queryOptions?: QueryHookOptions,
	queryRef?: React.MutableRefObject<any>,
	debug?: boolean,
	useLoadingComponent?: boolean,
	noItemValid?: boolean,
	
	queryResultObject?( data: ApolloQueryResult<any>['data'] ): any,
	
	onQueryComplete?( request: QueryResult, resultingDataObject: any ): void,
	
	onQueryLayoutComplete?( request: QueryResult, resultingDataObject: any ): void,
	
	loadingComponent?(): any,
	
	errorComponent?( errors: ApolloError, query: GQLNode ): any,
	
	noItemComponent?: Function | ReactNode,
	
	[ additionalProps: string ]: any
}

const defaultProps: ItemFromQueryProps = {
	debug:                 false,
	queryRef:              null,
	query:                 null,
	queryOptions:          {},
	useLoadingComponent:   true,
	queryResultObject:     data => data,
	onQueryComplete:       null,
	onQueryLayoutComplete: null,
	loadingComponent:      () => ( <LoadingIndicator isActive={ true } dimmer={ false }/> ),
	errorComponent:        ( response, query ) => <ErrorMessageFromGraphQL response={ response } query={ query }/>,
	noItemComponent:       null,
	noItemValid:           false,
	as:                    'div'
}

const ItemFromQuery: FC<ItemFromQueryProps> = ( props ) => {
	
	let itemReq = useQuery( props.query, props.queryOptions );
	
	useEffect( () => {
		
		if( props.queryRef ){
			props.queryRef.current = itemReq;
		}
		
		if( props.debug ){
			console.log( itemReq );
		}
		
		if( queriesHaveData( itemReq ) || queriesHaveErrors( itemReq ) ){
			
			runIfFunction( props.onQueryComplete, itemReq, props.queryResultObject( itemReq.data ) );
		}
		
	}, [ itemReq ] );
	
	useLayoutEffect( () => {
		
		if( queriesHaveData( itemReq ) && !queriesHaveErrors( itemReq ) ){
			
			runIfFunction( props.onQueryLayoutComplete, itemReq, props.queryResultObject( itemReq.data ) );
		}
		
	}, [ itemReq ] );
	
	if( queriesLoading( itemReq ) && props.useLoadingComponent ){
		
		return _.isFunction( props.loadingComponent )
		       ? props.loadingComponent()
		       : props.loadingComponent;
	}
	if( queriesHaveErrors( itemReq ) ){
		
		return _.isFunction( props.errorComponent )
		       ? props.errorComponent( itemReq.error, props.query )
		       : props.errorComponent;
	}
	if( !queriesHaveData( itemReq ) ){
		
		return _.isFunction( props.noItemComponent )
		       ? props.noItemComponent()
		       : props.noItemComponent;
	}
	
	let item = props.queryResultObject( itemReq.data );
	
	if( !props.noItemValid && !item || ( !props.noItemValid && _.isArray( item ) && item.length === 0 ) ){
		
		return _.isFunction( props.noItemComponent )
		       ? props.noItemComponent()
		       : props.noItemComponent;
	}
	
	// dynamic tag
	let Tag = props.as;
	
	return (
		<Tag { ..._.omit( props, _.keys( ItemFromQuery.defaultProps ) ) }>
			{ _.isFunction( props.children ) ? props.children( item ) : props.children }
		</Tag>
	);
};

ItemFromQuery.defaultProps = defaultProps;

export { ItemFromQuery }
